/**
 * Gudwin identity lockup — the mark plus the "Gudwin.ai" wordmark.
 *
 * The mark ships as a flat SVG (pure paths, no font dependency) so it renders
 * pixel-perfect through <img>. The wordmark is drawn as live HTML text instead
 * of the baked lockup SVG: an SVG loaded via <img> is font-sandboxed and would
 * fall back to a system sans, losing IBM Plex Sans Condensed. Rendering the
 * wordmark in the DOM lets it use the self-hosted brand faces
 * (--font-display for "Gudwin", --font-mono for the ".ai" accent).
 *
 * Source of truth for the asset set:
 * frontend/design-system/gudwin-design-system/brand/.
 */
import markUrl from "@/assets/brand/gudwin-mark.svg";
import { cn } from "@/lib/utils";

interface BrandLockupProps {
  /** Mark height in px; the wordmark scales from it. Default 22. */
  size?: number;
  /** Render just the mark, no wordmark. Default false. */
  markOnly?: boolean;
  className?: string;
}

export function BrandLockup({
  size = 22,
  markOnly = false,
  className,
}: BrandLockupProps): React.ReactElement {
  return (
    <span
      className={cn("inline-flex items-center", className)}
      style={{ gap: Math.round(size * 0.34) }}
    >
      <img src={markUrl} alt="Gudwin" style={{ height: size, width: "auto" }} draggable={false} />
      {!markOnly && (
        <span
          aria-hidden
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 700,
            fontSize: Math.round(size * 0.98),
            lineHeight: 1,
            letterSpacing: "-0.01em",
            color: "var(--fg-1)",
          }}
        >
          Gudwin
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontWeight: 700,
              fontSize: Math.round(size * 0.46),
              color: "var(--heat-bright)",
            }}
          >
            .ai
          </span>
        </span>
      )}
    </span>
  );
}
